import { useCallback, useState } from "react";
import {
  listAdminUsersReq,
  listRolesReq,
  createAdminUserReq,
  updateAdminUserReq,
  archiveAdminUserReq,
} from "services/api";
import { USER_MANAGEMENT_MESSAGES } from "./userManagement.constants";

const parseErrorMessage = (err, fallbackMessage) => {
  const data = err?.response?.data;
  if (typeof data === "string" && data) return data;
  return data?.message || data?.errors?.[0]?.msg || fallbackMessage;
};

function useAdminUsers(adminId) {
  const [adminUsers, setAdminUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!adminId) return;
    setIsLoading(true);
    try {
      const [usersRes, rolesRes] = await Promise.all([
        listAdminUsersReq({ admin_id: adminId }),
        listRolesReq({ admin_id: adminId }),
      ]);
      setAdminUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      setRoles(Array.isArray(rolesRes.data) ? rolesRes.data : []);
    } catch (err) {
      setErrorMessage(parseErrorMessage(err, "Failed to load users."));
    }
    setIsLoading(false);
  }, [adminId]);

  const save = async (request, successText, failText) => {
    try {
      setErrorMessage(null);
      setSuccessMessage(null);
      await request();
      setSuccessMessage(successText);
      await fetchAll();
      return true;
    } catch (err) {
      setErrorMessage(parseErrorMessage(err, failText));
      return false;
    }
  };

  const createAdminUser = (body) =>
    save(
      () => createAdminUserReq({ admin_id: adminId, body }),
      USER_MANAGEMENT_MESSAGES.created,
      "Failed to create user."
    );

  const updateAdminUser = (id, body) =>
    save(
      () => updateAdminUserReq({ admin_id: adminId, id, body }),
      USER_MANAGEMENT_MESSAGES.updated,
      "Failed to update user."
    );

  const deleteAdminUser = (id) =>
    save(
      () => archiveAdminUserReq({ admin_id: adminId, id }),
      USER_MANAGEMENT_MESSAGES.deleted,
      "Failed to delete user."
    );

  return {
    adminUsers,
    roles,
    isLoading,
    errorMessage,
    successMessage,
    fetchAll,
    createAdminUser,
    updateAdminUser,
    deleteAdminUser,
    clearError: () => setErrorMessage(null),
    clearSuccess: () => setSuccessMessage(null),
  };
}

export default useAdminUsers;
